import './FairPlay.css'

export default function FairPlay() {
  return (
    <div className="fairplay-container">
      <div className="fairplay-header">
        <div className="fairplay-logo">
          <img
            src="/images/logo/finallogosquare.png"
            alt="RiftChess"
            className="logo-icon"
          />
        </div>
        <h1>Cheating & Fair Play</h1>
        <p>Leveling the playing field through innovative game design</p>
      </div>

      <div className="fairplay-content">
        <section className="fairplay-section">
          <h2>Why Traditional Cheating Doesn't Work in RiftChess</h2>

          <div className="fairplay-highlight">
            <h3>Engine Analysis Becomes Useless</h3>
            <p>In RiftChess, traditional chess engines like Stockfish cannot effectively analyze positions because the <strong>special squares add enough variance and random outcomes</strong> that analyzing moves ahead becomes unreliable. The dynamic nature of the battlefield means that even the most sophisticated chess analysis engines can't predict the evolving game state.</p>
          </div>

          <div className="fairplay-advantage">
            <h3>Random Square Generation</h3>
            <p>The placement of special squares is <strong>completely random</strong>, following the rules displayed on our Rules page. This randomness ensures that:</p>
            <ul>
              <li>No pre-calculated engine lines can predict future board states</li>
              <li>Players must adapt in real-time to changing conditions</li>
              <li>Strategic thinking becomes more important than memorized patterns</li>
              <li>The game rewards creativity over computation</li>
            </ul>
          </div>
        </section>

        <section className="fairplay-section">
          <h2>Leveling the Playing Field</h2>

          <div className="fairplay-philosophy">
            <h3>Our Game Philosophy</h3>
            <p>RiftChess promotes <strong>creativity and adaptation</strong> because the tables can turn in a single move. Our goal is to level the playing field where lower-skilled players still have a chance to win games against top-skilled players once in a while.</p>
          </div>

          <div className="fairplay-balance">
            <h3>Skill vs. Chance Balance</h3>
            <div className="balance-explanation">
              <div className="balance-item">
                <strong>Higher skill players</strong> will obviously win more in the long term through superior strategic understanding and tactical awareness.
              </div>
              <div className="balance-item">
                <strong>Every player</strong> now has a genuine chance to win a tournament or gaming session, compared to classical chess where it's almost impossible to beat significantly higher ELO players.
              </div>
            </div>
          </div>
        </section>

        <section className="fairplay-section">
          <h2>Dynamic Gameplay Elements</h2>

          <div className="dynamics-grid">
            <div className="dynamic-item">
              <h4>Unpredictable Turns</h4>
              <p>Special squares can dramatically alter the game state, making each turn a new strategic challenge.</p>
            </div>
            <div className="dynamic-item">
              <h4>Adaptive Strategy</h4>
              <p>Players must constantly reassess their position and adapt their strategy to emerging threats and opportunities.</p>
            </div>
            <div className="dynamic-item">
              <h4>Creative Solutions</h4>
              <p>The variance introduced by special squares rewards innovative thinking over rote memorization.</p>
            </div>
            <div className="dynamic-item">
              <h4>Fair Competition</h4>
              <p>Random elements ensure that every game starts with equal uncertainty for both players.</p>
            </div>
          </div>
        </section>

        <section className="fairplay-section">
          <h2>Enhanced Entertainment Value</h2>

          <div className="entertainment-content">
            <h3>More Exciting for Viewers and Streamers</h3>
            <p>The unpredictable nature of RiftChess special squares makes games <strong>significantly more interesting to watch and stream</strong>. Traditional chess can become predictable for viewers, but RiftChess maintains suspense throughout the entire game.</p>

            <div className="entertainment-benefits">
              <div className="entertainment-item">
                <h4>Constant Tension</h4>
                <p>Viewers never know when a special square might completely change the game's momentum, keeping everyone on the edge of their seats.</p>
              </div>
              <div className="entertainment-item">
                <h4>Unpredictable Outcomes</h4>
                <p>Even when one player seems to be winning decisively, special squares can create dramatic comebacks that make for thrilling content.</p>
              </div>
              <div className="entertainment-item">
                <h4>Strategic Commentary</h4>
                <p>Streamers and commentators have more dynamic content to discuss as players adapt to evolving board conditions in real-time.</p>
              </div>
              <div className="entertainment-item">
                <h4>Accessible Viewing</h4>
                <p>New viewers can more easily follow and enjoy games since the random elements level the complexity, making it entertaining for all skill levels.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="fairplay-section">
          <h2>Our Commitment to Fair Play</h2>

          <div className="commitment-content">
            <p>RiftChess is designed from the ground up to create a fair and engaging experience for players of all skill levels. By introducing controlled randomness through special squares, we've created a game where:</p>

            <div className="commitment-points">
              <div className="commitment-point">
                <span className="point-icon">✅</span>
                <span>Traditional chess engines provide no meaningful advantage</span>
              </div>
              <div className="commitment-point">
                <span className="point-icon">✅</span>
                <span>Every player has the potential for exciting victories</span>
              </div>
              <div className="commitment-point">
                <span className="point-icon">✅</span>
                <span>Skill and strategy remain the primary factors in long-term success</span>
              </div>
              <div className="commitment-point">
                <span className="point-icon">✅</span>
                <span>Games remain unpredictable and engaging until the very end</span>
              </div>
            </div>
          </div>
        </section>

        <section className="fairplay-section">
          <h2>Reporting Issues</h2>

          <div className="reporting-info">
            <p>While our game design inherently prevents traditional forms of cheating, we're committed to maintaining a positive gaming environment. If you encounter any suspicious behavior or have concerns about fair play, please contact our support team.</p>

            <div className="contact-info">
              <a href="mailto:contact@riftchess.com" className="contact-link">
                contact@riftchess.com
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}